import type { IndeedIntegrationSettings } from "api-clients/monolith";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Typography } from "@/components/ui/typography";
import globalMessages from "@/i18n/global-messages";
import { domainMeta } from "@/utils/domain-meta";
import { writeHeldToggles, type HeldIndeedToggles } from "@/utils/indeed-connect-storage";

import {
  getIndeedSettingsVisibility,
  IndeedSettingsFields,
  type IndeedSettingsValues,
} from "./indeed-settings-fields";

export function ConnectMode({
  settings,
  isOrganic,
  onClose,
}: {
  settings: IndeedIntegrationSettings;
  isOrganic: boolean;
  onClose: () => void;
}) {
  const intl = useIntl();
  const location = useLocation();
  const input = useRef<HTMLInputElement>(null);
  const { showTitleSource, showToggles } = getIndeedSettingsVisibility(settings);
  const [email, setEmail] = useState(settings.indeed_authorization_email ?? "");
  const [emailError, setEmailError] = useState(false);
  const [value, setValue] = useState<IndeedSettingsValues>({
    indeed_xml_feed_enabled: (isOrganic && showToggles) || settings.indeed_xml_feed_enabled,
    indeed_apply_enabled: settings.indeed_apply_enabled,
    sourcing_title_source: settings.sourcing_title_source,
  });

  function connect() {
    if (!email.trim()) {
      setEmailError(true);
      input.current?.focus();
      return;
    }
    const changed: HeldIndeedToggles["changed"] = {};
    if (showToggles && value.indeed_xml_feed_enabled !== settings.indeed_xml_feed_enabled)
      changed.indeed_xml_feed_enabled = value.indeed_xml_feed_enabled;
    if (showToggles && value.indeed_apply_enabled !== settings.indeed_apply_enabled)
      changed.indeed_apply_enabled = value.indeed_apply_enabled;
    if (showTitleSource && value.sourcing_title_source !== settings.sourcing_title_source)
      changed.sourcing_title_source = value.sourcing_title_source;
    writeHeldToggles(changed, Date.now());
    const params = new URLSearchParams({
      employer_auth_email: email.trim(),
      return_to: window.location.origin + (domainMeta.basename ?? "") + location.pathname,
    });
    window.location.assign(
      `${domainMeta.monolithOrigin}/redirect/job_boards/authorize_indeed?${params}`,
    );
  }

  const error = emailError
    ? intl.formatMessage({
        defaultMessage: "Enter your Indeed account email",
        description: "The Indeed account email is required before connecting",
      })
    : undefined;

  return (
    <>
      <SheetHeader>
        <SheetTitle>
          <FormattedMessage
            defaultMessage="Add Contract"
            description="Sheet title: Add a new contract"
          />
        </SheetTitle>
      </SheetHeader>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          connect();
        }}
        noValidate
      >
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1">
            <Typography as="h2" variant="h6">
              <FormattedMessage
                defaultMessage="Connect your Indeed account"
                description="Connect an employer's Indeed account to Fountain"
              />
            </Typography>
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Log in or sign up for an Indeed account to sponsor your jobs on Indeed and receive applicants in Fountain."
                description="Connecting Indeed lets employers sponsor jobs and receive applicants"
              />
            </Typography>
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Connecting your Indeed account enables access to analytics for your organic jobs and campaign and billing management for your sponsored jobs, directly in Indeed."
                description="Explains the analytics and management available after connecting Indeed"
              />
            </Typography>
          </div>
          <Input
            ref={input}
            id="indeed-account-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            label={intl.formatMessage({
              defaultMessage: "Indeed account email",
              description: "Email address used to sign in to the employer Indeed account",
            })}
            description={intl.formatMessage({
              defaultMessage: "The email you use to log in to Indeed",
              description: "Use the email associated with the employer Indeed account",
            })}
            placeholder={intl.formatMessage({
              defaultMessage: "Enter your Indeed account email",
              description: "The Indeed account email is required before connecting",
            })}
            error={error}
            showError={false}
            aria-invalid={emailError}
            aria-describedby={emailError ? "indeed-email-error" : undefined}
          />
          {error && (
            <Typography
              variant="small1"
              className="text-fx-rose"
              role="alert"
              id="indeed-email-error"
            >
              {error}
            </Typography>
          )}
          <IndeedSettingsFields settings={settings} value={value} onChange={setValue} />
          <Separator />
          <Typography variant="subtitle1">
            <FormattedMessage
              defaultMessage="Need help? <link>Contact Indeed Support</link> for issues such as account linking support and job posting visibility troubleshooting."
              description="Indeed support helps with account linking and job visibility"
              values={{
                link: (chunks) => (
                  <a
                    className="text-fx-brand-600 underline"
                    href="https://indeed.my.site.com/employerSupport1/s/article/207325006"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </Typography>
        </div>
        <SheetFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button type="submit">
            <FormattedMessage
              defaultMessage="Connect Indeed"
              description="Start connecting the employer Indeed account"
            />
          </Button>
        </SheetFooter>
      </form>
    </>
  );
}
